import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Bar, Line } from 'react-chartjs-2';
import { Alert, Button, Skeleton } from '@mui/material';
import PointOfSaleOutlinedIcon from '@mui/icons-material/PointOfSaleOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import { SERIES, barStyle, lineStyle, categoryOptions, rankOptions, formatNumber } from '../comp/custchart/chartTheme';
import Heatmap from '../comp/custchart/Heatmap';
import '../css/dashboard.css';

const BRAND = '#0d4c92';

const sum = (arr) => (arr || []).reduce((a, b) => a + (Number(b) || 0), 0);

// "₮1,234" — every ChartBoard measure is a money amount.
const money = (v) => (Number(v) < 0 ? '-' : '') + '₮' + formatNumber(Math.abs(Number(v) || 0));

// Ranked horizontal bars: one row per entity, sorted high → low.
const ranked = (labels, values, label, color) => {
    const rows = labels.map((l, i) => ({ l, v: Number(values[i]) || 0 })).sort((a, b) => b.v - a.v);
    return {
        count: rows.length,
        data: {
            labels: rows.map((r) => r.l),
            datasets: [{ label, data: rows.map((r) => r.v), ...barStyle(color), maxBarThickness: 18 }],
        },
    };
};

// Ranked charts grow with their row count so every name stays readable.
const rankHeight = (count) => Math.max(240, count * 30 + 40);

const StatTile = ({ label, value, icon, tone, hint }) => (
    <div className="dbtile" title={hint}>
        <div className={"dbtileicon " + tone}>{icon}</div>
        <div className="dbtilebody">
            <div className="dbtilelabel">{label}</div>
            <div className="dbtilevalue">{value}</div>
        </div>
    </div>
);

const ChartCard = ({ title, subtitle, children, height = 300 }) => (
    <section className="dbcard">
        <header className="dbcardhead">
            <h2>{title}</h2>
            {subtitle ? <span>{subtitle}</span> : <></>}
        </header>
        <div className="dbchart" style={height ? { height } : undefined}>{children}</div>
    </section>
);

const Empty = () => <div className="dbempty">Мэдээлэл алга</div>;

const Dashboard = ({ token }) => {
    const [resp, setResp] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        setError(false);
        const request = new Request('https://wms-cloud.com/api/ChartBoard', {
            method: 'GET',
            headers: new Headers({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
                'Access-Control-Allow-Headers': 'X-Total-Count'
            })
        });
        fetch(request)
            .then((response) => {
                if (response.status == 200) {
                    return response.json();
                }
                throw new Error(response.status);
            })
            .then((r) => {
                setResp(r);
                setLoading(false);
            })
            .catch(() => {
                setError(true);
                setLoading(false);
            });
    }, [token]);

    useEffect(() => { load(); }, [load]);

    // Monthly flow. Backend: txn_type 'C' = татан авалт (бараа орсон) →
    // crdrreportlabel/crdrreportdata; 'D' = борлуулалт (захиалгаар гарсан) →
    // label2/data2. The two month lists come from separate queries, so they
    // are merged by month instead of assumed to line up.
    const flow = useMemo(() => {
        const inLabels = resp?.crdrreportlabel || [];
        const inValues = resp?.crdrreportdata || [];
        const outLabels = resp?.label2 || [];
        const outValues = resp?.data2 || [];
        const byMonth = (labels, values) => new Map(labels.map((l, i) => [l, Number(values[i]) || 0]));
        const inMap = byMonth(inLabels, inValues);
        const outMap = byMonth(outLabels, outValues);
        const labels = [...new Set([...inLabels, ...outLabels])].sort();
        const purchases = labels.map((l) => inMap.get(l) || 0);
        const sales = labels.map((l) => outMap.get(l) || 0);
        // Many periods read better as a trend line, a few as grouped bars.
        const asLine = labels.length > 12;
        const style = asLine ? lineStyle : barStyle;
        return {
            asLine,
            purchases: sum(purchases),
            sales: sum(sales),
            data: {
                labels,
                datasets: [
                    { label: 'Борлуулалт', data: sales, ...style(SERIES[1]) },
                    { label: 'Татан авалт', data: purchases, ...style(SERIES[0]) },
                ],
            },
        };
    }, [resp]);

    // companyBarData: one dataset per company, its monthly борлуулалт aligned to
    // companyBarData.labels (months).
    const company = useMemo(() => {
        const src = resp?.companyBarData || {};
        const rows = (src.datasets || []).map((d) => ({ label: d.label, values: (d.data || []).map(Number) }));
        return { columns: src.labels || [], rows, count: rows.length };
    }, [resp]);

    // Stock value (үнэ × үлдэгдэл) per company.
    const stock = useMemo(() => {
        const labels = resp?.productPielabel || [];
        const values = resp?.productPiedata || [];
        return { total: sum(values), ...ranked(labels, values, 'Үлдэгдэл', BRAND) };
    }, [resp]);

    if (error) {
        return <div className="dbroot">
            <Alert
                severity="error"
                action={<Button color="inherit" size="small" startIcon={<RefreshIcon />} onClick={load}>Дахин ачаалах</Button>}
            >
                Хяналтын самбарын мэдээллийг ачаалж чадсангүй.
            </Alert>
        </div>
    }

    if (loading) {
        return <div className="dbroot">
            <div className="dbtiles">
                {[0, 1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={88} />)}
            </div>
            <Skeleton variant="rounded" height={360} />
            <div className="dbgrid">
                <Skeleton variant="rounded" height={340} />
                <Skeleton variant="rounded" height={340} />
            </div>
        </div>
    }

    const net = flow.sales - flow.purchases;

    return <div className="dbroot">
        <div className="dbtiles">
            <StatTile label="Нийт борлуулалт" value={money(flow.sales)} tone="orange" icon={<PointOfSaleOutlinedIcon />} />
            <StatTile label="Нийт татан авалт" value={money(flow.purchases)} tone="blue" icon={<LocalShippingOutlinedIcon />} />
            <StatTile label="Зөрүү" hint="Борлуулалт ба татан авалтын зөрүү" value={money(Math.abs(net))} tone="neutral" icon={<SwapVertIcon />} />
            <StatTile label="Агуулахын үлдэгдэл" value={money(stock.total)} tone="neutral" icon={<Inventory2OutlinedIcon />} />
        </div>

        <ChartCard title="Компаниар" subtitle={`Сар бүрийн борлуулалт · ${company.count} компани`} height={null}>
            {company.count === 0 || company.columns.length === 0 ? <Empty /> : <Heatmap columns={company.columns} rows={company.rows} unit="₮" />}
        </ChartCard>

        <div className="dbgrid">
            <ChartCard title="Борлуулалт, татан авалт" subtitle="Сараар" height={320}>
                {flow.data.labels.length === 0 ? <Empty /> :
                    flow.asLine
                        ? <Line options={categoryOptions()} data={flow.data} />
                        : <Bar options={categoryOptions()} data={flow.data} />}
            </ChartCard>
            <ChartCard title="Агуулахын үлдэгдэл" subtitle={`Компаниар, үнийн дүнгээр · ${stock.count} компани`} height={Math.max(320, rankHeight(stock.count))}>
                {stock.count === 0 ? <Empty /> : <Bar options={rankOptions()} data={stock.data} />}
            </ChartCard>
        </div>
    </div>
}

export default Dashboard;
